/**
 * Projectile & visual-effect system.
 *
 * Projectiles deal damage on hit; VFX are purely cosmetic (beams, flashes, explosions).
 */
import type { Labyrinth } from './Labyrinth';
import type { Enemy } from './Entity';
import type { DamageType } from './StatusEffect';
import type { SwarmManager } from './SwarmManager';

// ─── Projectile types ───────────────────────────────────────

export type ProjectileKind = 'bullet' | 'rocket' | 'bomb';

/** Options for unique weapon behaviors on projectiles */
export interface ProjectileOptions {
  pierceCount?: number;
  homingStrength?: number;
  homingTarget?: Enemy;
  fragmentCount?: number;
  speedMult?: number;
  bonusDamageMult?: number;
  knockback?: number;
  pullStrength?: number;
  damageFalloff?: boolean;
  lingerDuration?: number;
  lingerDps?: number;
  /** Optional secondary effect. `| undefined` so call sites can write
   *  `statusEffect: someNullable ?? undefined` under exactOptionalPropertyTypes. */
  statusEffect?: { type: 'burn' | 'slow' | 'stun'; duration: number; magnitude: number; source: DamageType } | undefined;
}

export interface Projectile {
  kind: ProjectileKind;
  x: number;
  y: number;
  vx: number;          // tiles/sec
  vy: number;
  speed: number;       // tiles/sec (magnitude, for reference)
  damage: number;
  aoeRadius: number;   // 0 = single-target
  knockback: number;   // tiles to push on hit
  life: number;        // seconds remaining
  maxLife: number;
  /** For bomb arcing — normalised progress 0→1 */
  arcHeight: number;
  /** Source weapon category — used for rendering and damage type */
  damageType: DamageType;
  /** Weapon tier (1-5) — affects visual rendering */
  tier: number;
  // Unique weapon behavior fields
  /** Remaining enemies this projectile can pierce through (0 = die on first hit) */
  pierceRemaining: number;
  /** Set of enemies already pierced (to avoid re-hitting) */
  piercedEnemies: Set<Enemy>;
  /** Homing turn rate in rad/sec (0 = no homing) */
  homingStrength: number;
  /** Target enemy for homing */
  homingTarget: Enemy | null;
  /** Number of fragments to spawn on final hit */
  fragmentCount: number;
  /** Bonus damage multiplier */
  bonusDamageMult: number;
  /** Pull strength — reverse knockback toward center on AOE */
  pullStrength: number;
  /** Whether AOE damage falls off from center to edge */
  damageFalloff: boolean;
  /** Lingering damage zone duration (0 = none) */
  lingerDuration: number;
  /** Lingering damage zone DPS */
  lingerDps: number;
  /** Status effect to apply on hit */
  statusEffect: { type: 'burn' | 'slow' | 'stun'; duration: number; magnitude: number; source: DamageType } | null;
}

/** Lingering damage zone left by certain weapons */
export interface LingerZone {
  x: number;
  y: number;
  radius: number;
  dps: number;
  remaining: number;
  damageType: DamageType;
  tickTimer: number;
  color: number;
}

// ─── Visual effects ─────────────────────────────────────────

// ─── Enemy projectiles ─────────────────────────────────────

export type EnemyProjectileKind = 'bullet' | 'bomb';

export interface EnemyProjectile {
  kind: EnemyProjectileKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  speed: number;
  damage: number;
  life: number;
  maxLife: number;
  /** AoE radius for bombs (0 = single-target bullet) */
  aoeRadius: number;
  /** Target position for arcing bombs */
  targetX: number;
  targetY: number;
  /** Normalized arc progress 0→1 for bombs */
  arcHeight: number;
}

export type VfxKind = 'muzzle_flash' | 'explosion' | 'beam' | 'melee_slash' | 'death_explosion' | 'robot_hit';

export interface VfxEffect {
  kind: VfxKind;
  x: number;
  y: number;
  /** For beams: end point */
  tx?: number;
  ty?: number;
  /** For explosions: max radius in tiles */
  radius: number;
  life: number;
  maxLife: number;
  color: number;
}

// ─── Factory helpers ────────────────────────────────────────

export function createBullet(
  x: number, y: number, angle: number,
  damage: number, range: number, tier: number = 1,
  opts: ProjectileOptions = {},
): Projectile {
  const speed = 18 * (opts.speedMult ?? 1);
  return {
    kind: 'bullet',
    x, y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    speed,
    damage,
    aoeRadius: 0,
    knockback: opts.knockback ?? 0,
    life: range / speed,
    maxLife: range / speed,
    arcHeight: 0,
    damageType: 'ballistic',
    tier,
    pierceRemaining: opts.pierceCount ?? 0,
    piercedEnemies: new Set(),
    homingStrength: 0,
    homingTarget: null,
    fragmentCount: opts.fragmentCount ?? 0,
    bonusDamageMult: opts.bonusDamageMult ?? 1,
    pullStrength: 0,
    damageFalloff: false,
    lingerDuration: 0,
    lingerDps: 0,
    statusEffect: opts.statusEffect ?? null,
  };
}

export function createRocket(
  x: number, y: number, angle: number,
  damage: number, range: number, aoeRadius: number, knockback: number,
  tier: number = 1,
  opts: ProjectileOptions = {},
): Projectile {
  const speed = 7 * (opts.speedMult ?? 1);
  return {
    kind: 'rocket',
    x, y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    speed,
    damage,
    aoeRadius,
    knockback,
    life: range / speed,
    maxLife: range / speed,
    arcHeight: 0,
    damageType: 'rocket',
    tier,
    pierceRemaining: 0,
    piercedEnemies: new Set(),
    homingStrength: opts.homingStrength ?? 0,
    homingTarget: opts.homingTarget ?? null,
    fragmentCount: 0,
    bonusDamageMult: opts.bonusDamageMult ?? 1,
    pullStrength: 0,
    damageFalloff: false,
    lingerDuration: opts.lingerDuration ?? 0,
    lingerDps: opts.lingerDps ?? 0,
    statusEffect: opts.statusEffect ?? null,
  };
}

export function createBomb(
  x: number, y: number, tx: number, ty: number,
  damage: number, aoeRadius: number, tier: number = 1,
  opts: ProjectileOptions = {},
): Projectile {
  const dist = Math.hypot(tx - x, ty - y);
  const flightTime = 0.6 + dist * 0.15;  // slower arc
  return {
    kind: 'bomb',
    x, y,
    vx: (tx - x) / flightTime,
    vy: (ty - y) / flightTime,
    speed: dist / flightTime,
    damage,
    aoeRadius,
    knockback: opts.knockback ?? 0,
    life: flightTime,
    maxLife: flightTime,
    arcHeight: 1,
    damageType: 'bomb',
    tier,
    pierceRemaining: 0,
    piercedEnemies: new Set(),
    homingStrength: 0,
    homingTarget: null,
    fragmentCount: 0,
    bonusDamageMult: opts.bonusDamageMult ?? 1,
    pullStrength: opts.pullStrength ?? 0,
    damageFalloff: opts.damageFalloff ?? false,
    lingerDuration: opts.lingerDuration ?? 0,
    lingerDps: opts.lingerDps ?? 0,
    statusEffect: opts.statusEffect ?? null,
  };
}

// ─── VFX factories ──────────────────────────────────────────

export function createMuzzleFlash(x: number, y: number, color: number = 0xffcc44): VfxEffect {
  return { kind: 'muzzle_flash', x, y, radius: 0.3, life: 0.08, maxLife: 0.08, color };
}

export function createExplosion(x: number, y: number, radius: number, color: number = 0xff6600): VfxEffect {
  return { kind: 'explosion', x, y, radius, life: 0.35, maxLife: 0.35, color };
}

export function createBeam(
  x: number, y: number, tx: number, ty: number, color: number = 0x44ccff,
): VfxEffect {
  return { kind: 'beam', x, y, tx, ty, radius: 0, life: 0.15, maxLife: 0.15, color };
}

export function createMeleeSlash(x: number, y: number, color: number = 0xffaa00): VfxEffect {
  return { kind: 'melee_slash', x, y, radius: 0.5, life: 0.2, maxLife: 0.2, color };
}

export function createDeathExplosion(x: number, y: number, color: number = 0xff6600): VfxEffect {
  return { kind: 'death_explosion', x, y, radius: 1.2, life: 0.7, maxLife: 0.7, color };
}

export function createEnemyBullet(
  x: number, y: number, tx: number, ty: number, damage: number,
): EnemyProjectile {
  const speed = 10; // tiles/sec — slower than player bullets, visible
  const dx = tx - x;
  const dy = ty - y;
  const dist = Math.hypot(dx, dy);
  const nx = dx / (dist || 1);
  const ny = dy / (dist || 1);
  const range = dist + 1; // overshoot slightly
  return {
    kind: 'bullet',
    x, y,
    vx: nx * speed,
    vy: ny * speed,
    speed,
    damage,
    life: range / speed,
    maxLife: range / speed,
    aoeRadius: 0,
    targetX: tx,
    targetY: ty,
    arcHeight: 0,
  };
}

/** Create an arcing enemy bomb projectile that explodes in AoE on landing */
export function createEnemyBomb(
  x: number, y: number, tx: number, ty: number,
  damage: number, aoeRadius: number,
): EnemyProjectile {
  const dist = Math.hypot(tx - x, ty - y);
  const flightTime = 0.6 + dist * 0.15;
  return {
    kind: 'bomb',
    x, y,
    vx: (tx - x) / flightTime,
    vy: (ty - y) / flightTime,
    speed: dist / flightTime,
    damage,
    life: flightTime,
    maxLife: flightTime,
    aoeRadius,
    targetX: tx,
    targetY: ty,
    arcHeight: 1,
  };
}

export function createRobotHit(x: number, y: number): VfxEffect {
  return { kind: 'robot_hit', x, y, radius: 0.5, life: 0.25, maxLife: 0.25, color: 0xff2222 };
}

// ─── Update logic ───────────────────────────────────────────

export interface ProjectileHit {
  projectile: Projectile;
  enemy: Enemy;
  x: number;
  y: number;
  /** Damage multiplier (1.0 = full, used for falloff) */
  damageMultiplier: number;
}

/**
 * Advance all projectiles, check collisions with enemies and walls.
 * Returns hits (caller applies damage/knockback) and spawns explosion VFX.
 * Removes dead projectiles in-place.
 */
export function updateProjectiles(
  projectiles: Projectile[],
  dt: number,
  enemies: readonly Enemy[],
  labyrinth: Labyrinth,
  vfx: VfxEffect[],
  lingerZones: LingerZone[],
  hitsOut?: ProjectileHit[],
  swarm?: SwarmManager | null,
): ProjectileHit[] {
  const hits = hitsOut ?? [];
  hits.length = 0;

  for (let i = projectiles.length - 1; i >= 0; i--) {
    const p = projectiles[i]!;
    p.life -= dt;

    // Homing steering (rockets)
    if (p.homingStrength > 0 && p.homingTarget && !p.homingTarget.alive) {
      p.homingTarget = null; // release dead target for GC
    }
    if (p.homingStrength > 0 && p.homingTarget) {
      const targetAngle = Math.atan2(p.homingTarget.y - p.y, p.homingTarget.x - p.x);
      const currentAngle = Math.atan2(p.vy, p.vx);
      let angleDiff = targetAngle - currentAngle;
      angleDiff = angleDiff % (Math.PI * 2);
      if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      const maxTurn = p.homingStrength * dt;
      const turn = Math.max(-maxTurn, Math.min(maxTurn, angleDiff));
      const newAngle = currentAngle + turn;
      p.vx = Math.cos(newAngle) * p.speed;
      p.vy = Math.sin(newAngle) * p.speed;
    }

    // Move
    p.x += p.vx * dt;
    p.y += p.vy * dt;

    // Wall collision (bullets and rockets die on walls, bombs fly over)
    if (p.kind !== 'bomb') {
      const tx = Math.floor(p.x);
      const ty = Math.floor(p.y);
      if (!labyrinth.isWalkable(tx, ty)) {
        // Rocket explodes on wall
        if (p.kind === 'rocket' && p.aoeRadius > 0) {
          applyAoE(p, enemies, hits, vfx, lingerZones, swarm);
        }
        projectiles.splice(i, 1);
        continue;
      }
    }

    // Life expired
    if (p.life <= 0) {
      // Bomb and rocket explode at end of life
      if (p.aoeRadius > 0) {
        applyAoE(p, enemies, hits, vfx, lingerZones, swarm);
      }
      projectiles.splice(i, 1);
      continue;
    }

    // Direct hit check (bullets and rockets — not bombs, they only AoE)
    if (p.kind !== 'bomb') {
      let hitEnemy: Enemy | null = null;
      let hitDist = Infinity;
      const hitRadius = 0.4; // enemy collision radius in tiles

      for (const e of enemies) {
        if (!e.alive) continue;
        // Skip enemies already pierced by this projectile
        if (p.piercedEnemies.has(e)) continue;
        const d = Math.hypot(e.x - p.x, e.y - p.y);
        if (d < hitRadius && d < hitDist) {
          hitDist = d;
          hitEnemy = e;
        }
      }

      if (hitEnemy) {
        hits.push({ projectile: p, enemy: hitEnemy, x: p.x, y: p.y, damageMultiplier: 1 });

        if (p.aoeRadius > 0) {
          // Rocket: explode on direct hit
          applyAoE(p, enemies, hits, vfx, lingerZones, swarm);
          projectiles.splice(i, 1);
        } else if (p.pierceRemaining > 0) {
          // Piercing bullet — pass through this enemy
          p.pierceRemaining--;
          p.piercedEnemies.add(hitEnemy);
          vfx.push(createMuzzleFlash(p.x, p.y, 0xffdd66));
          // Don't splice — projectile continues
        } else {
          // Normal bullet: single target, small flash
          vfx.push(createMuzzleFlash(p.x, p.y, 0xffdd66));
          projectiles.splice(i, 1);
        }
        continue;
      }

      // Swarm enemy direct hit check (if no regular enemy was hit)
      if (swarm && swarm.aliveCount > 0) {
        const nearby = swarm.grid.query(p.x, p.y, hitRadius);
        let hitSwarmIdx = -1;
        let hitSwarmDist = Infinity;
        for (let n = 0; n < nearby.length; n++) {
          const si = nearby[n]!;
          if (!swarm.alive[si]) continue;
          const d = Math.hypot(swarm.x[si]! - p.x, swarm.y[si]! - p.y);
          if (d < hitRadius && d < hitSwarmDist) {
            hitSwarmDist = d;
            hitSwarmIdx = si;
          }
        }
        if (hitSwarmIdx >= 0) {
          const dmg = p.damage * p.bonusDamageMult;
          swarm.damage_enemy(hitSwarmIdx, dmg);

          if (p.aoeRadius > 0) {
            applyAoE(p, enemies, hits, vfx, lingerZones, swarm);
            projectiles.splice(i, 1);
          } else if (p.pierceRemaining > 0) {
            p.pierceRemaining--;
            vfx.push(createMuzzleFlash(p.x, p.y, 0xffdd66));
          } else {
            vfx.push(createMuzzleFlash(p.x, p.y, 0xffdd66));
            projectiles.splice(i, 1);
          }
          continue;
        }
      }
    }
  }

  return hits;
}

/** Apply AoE damage — finds all enemies in radius, creates explosion VFX.
 *  Also handles pull, damage falloff, and linger zone spawning. */
function applyAoE(
  p: Projectile,
  enemies: readonly Enemy[],
  hits: ProjectileHit[],
  vfx: VfxEffect[],
  lingerZones: LingerZone[],
  swarm?: SwarmManager | null,
) {
  const color = p.damageType === 'bomb' ? 0xff4400 : 0xff6600;
  vfx.push(createExplosion(p.x, p.y, p.aoeRadius, color));

  // Build a Set of enemies already hit by this projectile (O(1) lookup instead of O(n) scan)
  const alreadyHitByThis = new Set<Enemy>();
  for (const h of hits) {
    if (h.projectile === p) alreadyHitByThis.add(h.enemy);
  }

  for (const e of enemies) {
    if (!e.alive) continue;
    const d = Math.hypot(e.x - p.x, e.y - p.y);
    if (d <= p.aoeRadius) {
      // Check if this enemy wasn't already hit by the same projectile's direct hit
      if (!alreadyHitByThis.has(e)) {
        // Damage falloff: full at center, 50% at edge
        const mult = p.damageFalloff ? (1 - 0.5 * (d / p.aoeRadius)) : 1;
        hits.push({ projectile: p, enemy: e, x: p.x, y: p.y, damageMultiplier: mult });
      }

      // Pull enemies toward center (Gravity-Well Imploder)
      if (p.pullStrength > 0 && d > 0.1) {
        const pullDist = p.pullStrength * (1 - d / p.aoeRadius);
        const nx = (p.x - e.x) / d;
        const ny = (p.y - e.y) / d;
        e.x += nx * pullDist;
        e.y += ny * pullDist;
      }
    }
  }

  // AoE also damages swarm enemies
  if (swarm && swarm.aliveCount > 0) {
    const nearby = swarm.grid.query(p.x, p.y, p.aoeRadius);
    const dmg = p.damage * p.bonusDamageMult;
    for (let n = 0; n < nearby.length; n++) {
      const si = nearby[n]!;
      if (!swarm.alive[si]) continue;
      const d = Math.hypot(swarm.x[si]! - p.x, swarm.y[si]! - p.y);
      if (d <= p.aoeRadius) {
        const mult = p.damageFalloff ? (1 - 0.5 * (d / p.aoeRadius)) : 1;
        swarm.damage_enemy(si, dmg * mult);
      }
    }
  }

  // Spawn lingering damage zone
  if (p.lingerDuration > 0 && p.lingerDps > 0) {
    lingerZones.push({
      x: p.x,
      y: p.y,
      radius: p.aoeRadius,
      dps: p.lingerDps,
      remaining: p.lingerDuration,
      damageType: p.damageType,
      tickTimer: 0,
      color,
    });
  }
}

/**
 * Advance enemy projectiles, check collision with robot.
 * Returns total damage dealt this frame. Removes dead projectiles in-place.
 */
export function updateEnemyProjectiles(
  projectiles: EnemyProjectile[],
  dt: number,
  robotX: number,
  robotY: number,
  labyrinth: Labyrinth,
  vfx: VfxEffect[],
): number {
  let totalDamage = 0;
  const hitRadius = 0.4; // robot collision radius

  for (let i = projectiles.length - 1; i >= 0; i--) {
    const p = projectiles[i]!;
    p.life -= dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;

    // Bombs fly over walls; bullets die on wall contact
    if (p.kind !== 'bomb') {
      const tx = Math.floor(p.x);
      const ty = Math.floor(p.y);
      if (!labyrinth.isWalkable(tx, ty)) {
        projectiles.splice(i, 1);
        continue;
      }
    }

    // Life expired
    if (p.life <= 0) {
      if (p.kind === 'bomb' && p.aoeRadius > 0) {
        // Bomb explodes at target — check distance from robot
        vfx.push(createExplosion(p.targetX, p.targetY, p.aoeRadius, 0x9933cc));
        const d = Math.hypot(robotX - p.targetX, robotY - p.targetY);
        if (d <= p.aoeRadius) {
          // Damage falloff: full at center, 50% at edge
          const mult = 1 - 0.5 * (d / p.aoeRadius);
          totalDamage += p.damage * mult;
          vfx.push(createRobotHit(robotX, robotY));
        }
      }
      projectiles.splice(i, 1);
      continue;
    }

    // Direct hit robot check (bullets only — bombs only do AoE)
    if (p.kind !== 'bomb') {
      const d = Math.hypot(robotX - p.x, robotY - p.y);
      if (d < hitRadius) {
        totalDamage += p.damage;
        vfx.push(createRobotHit(robotX, robotY));
        projectiles.splice(i, 1);
        continue;
      }
    }
  }

  return totalDamage;
}

/** Update VFX lifetimes, remove expired. */
export function updateVfx(vfx: VfxEffect[], dt: number) {
  for (let i = vfx.length - 1; i >= 0; i--) {
    const v = vfx[i]!;
    v.life -= dt;
    if (v.life <= 0) {
      vfx.splice(i, 1);
    }
  }
}
